/**
 * <marquee-strip>
 * 
 * Markup contract:
 *   <marquee-strip data-speed="40" data-direction="left" data-pause-hover>
 *     <div data-track> ...items... </div>
 *   </marquee-strip>
 *
 * Data attributes (in):
 *   data-speed       Pixels per second. Default 40.
 *   data-direction   'left' (default) or 'right'. Handled in CSS via
 *                    animation-direction: reverse.
 *   data-pause-hover Present = pause the animation on hover. CSS only.
 *
 * Data attributes (out):
 *   data-running     Set once the strip is measured and animating.
 *
 * CSS custom properties written to [data-strip]:
 *   --marquee-offset    Negative track width in px, the translateX target.
 *   --marquee-duration  Seconds for one track width at the chosen speed.
 *
 * Styles live in assets/tailwind_directives.css (@keyframes marqueeScroll).
 * Respects prefers-reduced-motion: no clones, no animation.
 */
if (!customElements.get('marquee-strip')) {
  class MarqueeStrip extends HTMLElement {
    connectedCallback() {
      if (this._ready) {
        this._observe();
        return;
      }
      this._ready = true;

      this._speed = parseFloat(this.dataset.speed) || 40;
      this._track = this.querySelector('[data-track]');
      if (!this._track) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this._buildStrip();
      this._measure();
      this._observe();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._resizeTimer) clearTimeout(this._resizeTimer);
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    /* Wrap the authored track in a flex strip we can translate as one unit. */
    _buildStrip() {
      const existing = this.querySelector('[data-strip]');
      if (existing) {
        this._strip = existing;
        return;
      }

      const strip = document.createElement('div');
      strip.dataset.strip = '';
      this._track.parentNode.insertBefore(strip, this._track);
      strip.appendChild(this._track);
      this._strip = strip;
    }

    _measure(isRetry) {
      if (!this._strip) return;

      const trackWidth = this._track.scrollWidth;

      // Fonts or images may not have laid out yet — try once more next frame.
      if (!trackWidth) {
        if (isRetry) return;
        this._raf = requestAnimationFrame(() => this._measure(true));
        return;
      }

      this._trackWidth = trackWidth;
      this._clonesNeeded = Math.ceil((this.offsetWidth * 2) / trackWidth) + 1;
      this._renderClones();

      this._strip.style.setProperty('--marquee-offset', `-${trackWidth}px`);
      this._strip.style.setProperty('--marquee-duration', `${trackWidth / this._speed}s`);
      this.setAttribute('data-running', '');
    }

    _renderClones() {
      this._strip.querySelectorAll('[data-track-clone]').forEach((clone) => clone.remove());

      for (let i = 0; i < this._clonesNeeded; i++) {
        const clone = this._track.cloneNode(true);
        clone.removeAttribute('data-track');
        clone.setAttribute('data-track-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        // Editor block handles must stay unique to the original track.
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
          el.removeAttribute('data-shopify-editor-block');
        });
        this._strip.appendChild(clone);
      }
    }

    _observe() {
      if (this._observer || !this._strip || !('ResizeObserver' in window)) return;

      this._observer = new ResizeObserver(() => {
        if (this._resizeTimer) clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this._onResize(), 150);
      });
      this._observer.observe(this);
    }

    _onResize() {
      const trackWidth = this._track.scrollWidth;
      if (!trackWidth) return;

      const clonesNeeded = Math.ceil((this.offsetWidth * 2) / trackWidth) + 1;
      if (trackWidth === this._trackWidth && clonesNeeded === this._clonesNeeded) return;

      this.removeAttribute('data-running');
      this._measure();
    }
  }

  customElements.define('marquee-strip', MarqueeStrip);
}
